"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@speakai/ui/button";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@speakai/ui/dialog";
import { AutoReload } from "./auto-reload";
import { Input } from "@speakai/ui/input";
import { useToast } from "@speakai/ui/toast";
import { useTranslation } from "react-i18next";
import { trackSentryRevenue, trackFunnel, trackFailure } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { usePaymentMethods, useBalance } from "@/features/subscription/payment-queries";
import { useQueryClient } from "@tanstack/react-query";
import * as paymentApi from "@/features/subscription/api";
import { dashboardKeys, paymentKeys, subscriptionKeys } from "@/queries/keys";
import { usePlatform } from "@/hooks/use-platform";
import { SelectSource } from "./select-source";
import { AddSource } from "./add-source";
import { ConnectPaypalButton } from "./connect-paypal-button";
import {
  useAddBalance,
  useCreateChargeByPaddle,
  useCreateTopupIntent,
} from "@/features/subscription/mutations";
import { confirmStripePayment } from "@/features/subscription/payment/stripe";
import { useCredits, formatCredits } from "@/features/subscription/hooks/use-credits";
import {
  ADD_BALANCE_MIN_USD,
  ADD_BALANCE_MAX_USD,
  ADD_BALANCE_TIERS_WEB,
  ADD_BALANCE_TIERS_MOBILE,
  PaymentSourceType,
  TransactionSourceType,
  TransactionSourceName,
  TransactionType,
} from "@/features/subscription/types";

interface AddBalanceProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newBalance: number) => void;
  defaultAmount?: number;
  description?: string;
  canManageBilling?: boolean;
  /** Surface attribution for funnel tracking (defaults to "settings"). */
  source?: string;
  arm?: string; // Optional experiment arm identifier — forwarded into trackSentryRevenue extras for funnel attribution.
}

export function AddBalance({
  open,
  onClose,
  onSuccess,
  defaultAmount = 30,
  description = "",
  canManageBilling = true,
  source = "settings",
  arm,
}: AddBalanceProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [amount, setAmount] = useState(defaultAmount);
  const [selectedSource, setSelectedSource] = useState<{
    id: string;
    type: "card" | "paddle" | "balance";
  } | null>(null);
  const [error, setError] = useState("");
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [autoReloadOpen, setAutoReloadOpen] = useState(false);
  // True only when the user explicitly clicked or arrow-keyed onto a source —
  // not when SelectSource's autoSelectDefault chose one. Drives whether the
  // freshly added PayPal can override the current selection.
  const userPickedRef = useRef(false);

  const addBalance = useAddBalance();
  const createChargeByPaddle = useCreateChargeByPaddle();
  const createTopupIntent = useCreateTopupIntent();
  const { data: stripeMethods = [] } = usePaymentMethods(PaymentSourceType.Stripe);
  const { data: paddleMethods = [] } = usePaymentMethods(PaymentSourceType.Paddle);
  const { data: balanceData } = useBalance();
  const canAddCard = stripeMethods.length < 2;
  const canAddPaypal = paddleMethods.length < 1;

  const queryClient = useQueryClient();
  // Synchronous double-submit guard. `isPending` only flips after a re-render, leaving a
  // window on the very first click where a second click creates a second real Paddle charge.
  const isSubmittingRef = useRef(false);
  const [paddlePending, setPaddlePending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const openTrackedRef = useRef(false);
  // Bridges the Stripe.js confirm window (plain async — not a TanStack mutation)
  // so the Submit button stays disabled across the full intent → confirm → credit
  // sequence. Prevents fast double-click double-charge.
  const [isStripeConfirming, setIsStripeConfirming] = useState(false);

  const { isMobile } = usePlatform();
  const presetAmounts = isMobile ? ADD_BALANCE_TIERS_MOBILE : ADD_BALANCE_TIERS_WEB;
  const { creditMode, remaining: creditsRemaining } = useCredits();

  // Reset only on open transition — a live `defaultAmount` recalc must not
  // silently clear an explicit user pick.
  useEffect(() => {
    if (open) userPickedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (open) setAmount(defaultAmount);
  }, [open, defaultAmount]);

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (open && !openTrackedRef.current) {
      openTrackedRef.current = true;
      pushDataLayer("Balance", "Open", undefined, undefined, { source });
      trackFunnel("payment", "balance_open", { source });
    }
    if (!open) openTrackedRef.current = false;
  }, [open, source]);

  /**
   * Paddle credits the wallet on the transaction.completed webhook, not on the charge call
   * (the charge returns `billed`, not paid). Measured settlement: ~6s. Poll the real balance
   * rather than trusting the charge response, which still carries the pre-top-up figure.
   */
  async function awaitPaddleSettlement(before: number): Promise<number | null> {
    const deadline = Date.now() + 30_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2_000));
      const res = await paymentApi.getBalance();
      const next = res.success ? Number(res.data?.balance ?? NaN) : NaN;
      if (Number.isFinite(next) && next > before) {
        await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
        await queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
        await queryClient.invalidateQueries({ queryKey: dashboardKeys.usage() });
        return next;
      }
    }
    return null;
  }

  async function handleSubmit() {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await submit();
    } finally {
      isSubmittingRef.current = false;
    }
  }

  async function submit() {
    setError("");

    if (amount < ADD_BALANCE_MIN_USD || amount > ADD_BALANCE_MAX_USD) {
      setError(
        t("PAYMENT.AMOUNT_OUT_OF_RANGE", {
          min: creditMode ? `${ADD_BALANCE_MIN_USD} credits` : `$${ADD_BALANCE_MIN_USD}`,
          max: creditMode
            ? `${ADD_BALANCE_MAX_USD.toLocaleString()} credits`
            : `$${ADD_BALANCE_MAX_USD.toLocaleString()}`,
          defaultValue: "Amount must be between {{min}} and {{max}}.",
        }),
      );
      return;
    }

    if (!selectedSource || selectedSource.type === "balance") {
      setError(t("PAYMENT.SELECT_CARD", "Please select a payment method"));
      return;
    }

    pushDataLayer("Balance", "PurchaseClick", undefined, undefined, { payment_method: selectedSource.type });

    if (selectedSource.type === "paddle") {
      let paddleResult;
      try {
        paddleResult = await createChargeByPaddle.mutateAsync({
          amount,
          description: "ADD_BALANCE",
        });
      } catch (err) {
        toast.error(
          t("PAYMENT.PADDLE_CHARGE_FAILED", "PayPal payment failed. Please try again."),
        );
        trackFailure("payment", "paddle_charge_fail", {
          error_reason: (err instanceof Error ? err.message : "paddle_charge_failed").slice(0, 200),
          charge_type: "add_balance",
          payment_method: "paddle",
        });
        pushDataLayer("Balance", "PurchaseFail", undefined, undefined, { failure_type: "paddle_charge_fail", payment_method: selectedSource?.type });
        return;
      }

      // The charge succeeded but has not settled; the response balance is the PRE-top-up
      // figure. Do not report revenue or hand it to callers until the wallet actually moves.
      pushDataLayer("Balance", "PurchaseInitiated", undefined, amount, {
        source,
        payment_method: "paddle",
        ...(arm ? { arm: arm.slice(0, 60) } : {}),
      });

      const before = Number.isFinite(Number(balanceData?.balance))
        ? Number(balanceData?.balance)
        : Number(paddleResult.data?.balance ?? 0);
      setPaddlePending(true);
      const settled = await awaitPaddleSettlement(before);
      setPaddlePending(false);

      if (settled === null) {
        // Charge is real; the credit is still in flight. Never call onSuccess with a stale
        // balance — an upload gate comparing `newBalance >= total` would silently no-op.
        toast.success(
          t("PAYMENT.PADDLE_PENDING", "Payment received. Your credits will appear shortly."),
        );
        onClose();
        return;
      }

      trackSentryRevenue("add_balance", amount, { source, payment_method: "paddle", ...(arm ? { arm } : {}) });
      pushDataLayer("Balance", "PurchaseSuccess", undefined, amount, {
        source,
        payment_method: selectedSource?.type ?? "card",
        ...(arm ? { arm: arm.slice(0, 60) } : {}),
      });
      onSuccess?.(settled);
      onClose();
      return;
    }

    // Card path — 3 sequential steps to charge a saved Stripe card:
    //   (1) server creates a Stripe PaymentIntent and returns clientSecret
    //   (2) Stripe.js confirms the PaymentIntent — this is the actual charge
    //   (3) server credits the user's balance on success
    // Card path: server creates Stripe PaymentIntent → Stripe.js confirms → server credits balance.
    let intent: Awaited<ReturnType<typeof createTopupIntent.mutateAsync>>;
    try {
      intent = await createTopupIntent.mutateAsync({
        totalAmount: amount,
        sourceId: selectedSource.id,
        sourceType: TransactionSourceType.ReloadBalance,
        sourceName: TransactionSourceName.ReloadBalance,
        transactionType: TransactionType.Card,
        description: "ADD_BALANCE",
      });
    } catch (err: unknown) {
      trackFailure("payment", "create_intent_failed", {
        error_reason: String(err).slice(0, 200),
        charge_type: "add_balance",
        payment_method: "card",
      });
      setError(
        t(
          "PAYMENT.PAYMENT_INTENT_FAILED",
          "Couldn't start payment. Please try again.",
        ),
      );
      return;
    }

    if (!intent.success || !intent.data?.clientSecret) {
      trackFailure("payment", "intent_create_fail", {
        error_reason: (intent.error ?? "no_client_secret").slice(0, 200),
        charge_type: "add_balance",
        payment_method: "card",
      });
      setError(
        t(
          "PAYMENT.PAYMENT_INTENT_FAILED",
          "Couldn't start payment. Please try again.",
        ),
      );
      return;
    }

    const returnUrl = `${window.location.origin}/profile/payment?state=stripe&source_component=add-balance&amount=${amount}`;

    try {
      setIsStripeConfirming(true);
      await confirmStripePayment(
        intent.data.clientSecret,
        selectedSource.id,
        returnUrl,
      );
    } catch (stripeErr: unknown) {
      const errMessage =
        stripeErr instanceof Error && stripeErr.message
          ? stripeErr.message.slice(0, 200)
          : t("PAYMENT.STRIPE_CHARGE_FAILED", "Payment failed. Please try another card.");
      trackFailure("payment", "stripe_confirm_fail", {
        error_reason: (stripeErr instanceof Error ? stripeErr.message : "stripe_error").slice(0, 200),
        charge_type: "add_balance",
        payment_method: "card",
      });
      setError(errMessage);
      return;
    } finally {
      setIsStripeConfirming(false);
    }

    try {
      const result = await addBalance.mutateAsync({ paymentIntentId: intent.data.id });

      if (!result.success || !result.data) {
        const errMsg = result.error ?? t("PAYMENT.PAYMENT_FAILED", "Payment failed");
        setError(errMsg);
        toast.error(errMsg);
        trackFailure("payment", "card_credit_fail", {
          error_reason: (result.error ?? "credit_failed").slice(0, 200),
          charge_type: "add_balance",
          payment_method: "card",
        });
        return;
      }

      trackSentryRevenue("add_balance", amount, {
        source,
        ...(arm ? { arm } : {}),
        payment_method: "card",
      });
      pushDataLayer("Balance", "PurchaseSuccess", undefined, amount, {
        source,
        payment_method: selectedSource?.type ?? "card",
        ...(arm ? { arm: arm.slice(0, 60) } : {}),
      });
      onSuccess?.(Number(result.data.balance));
      onClose();
    } catch (err) {
      const errMsg =
        err instanceof Error && err.message
          ? err.message
          : t("PAYMENT.PAYMENT_FAILED", "Payment failed");
      setError(errMsg);
      toast.error(errMsg);
      trackFailure("payment", "card_credit_fail", {
        error_reason: (err instanceof Error ? err.message : "credit_failed").slice(0, 200),
        charge_type: "add_balance",
        payment_method: "card",
      });
    }
  }

  const isPending =
    addBalance.isPending ||
    createChargeByPaddle.isPending ||
    createTopupIntent.isPending ||
    isStripeConfirming || paddlePending;

  return (
    <>
      <Dialog
        open={open && !addSourceOpen && !autoReloadOpen}
        onClose={onClose}
        aria-labelledby="add-balance-title"
        aria-modal="true"
        data-testid="add-balance-dialog"
      >
        <DialogHeader>
          {/* Single flex child — DialogHeader is `flex justify-between` so a
              bare h2 would wrap when helper text grows. */}
          <div className="flex flex-col gap-1 min-w-0 w-full">
            <h2 id="add-balance-title" className="text-lg font-semibold">
              {t("PAYMENT.ADD_BALANCE", "Add Credit")}
            </h2>

            <p className="text-xs text-muted-foreground">
              Your credit never expires. Use it for transcription, translation, and AI Chat.
            </p>

            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t("CREDITS.TOTAL", "Total credits")}</span>
              <span className="font-medium" data-sentry-mask>
                {formatCredits(creditMode ? creditsRemaining : (balanceData?.balance ?? 0))}
              </span>
            </div>
            <label htmlFor="add-balance-amount" className="text-sm font-medium">
              {t("PAYMENT.AMOUNT", "Amount")} {creditMode ? "(credits)" : "(USD)"}
            </label>
            <div className="flex flex-wrap gap-2" role="group" aria-label={`${t("PAYMENT.AMOUNT", "Amount")} presets`}>
              {presetAmounts.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant={amount === preset ? "primary" : "outline"}
                  size="sm"
                  onClick={() => setAmount(preset)}
                  aria-pressed={amount === preset}
                  disabled={isPending}
                >
                  {creditMode ? preset : `$${preset}`}
                </Button>
              ))}
            </div>
            <Input
              ref={inputRef}
              id="add-balance-amount"
              type="number"
              min={ADD_BALANCE_MIN_USD}
              max={ADD_BALANCE_MAX_USD}
              step={1}
              value={amount}
              aria-label={creditMode ? "Top-up amount in credits" : t("PAYMENT.AMOUNT", "Top-up amount in USD")}
              data-sentry-mask
              data-testid="add-balance-amount-input"
              disabled={isPending}
              onChange={(e) => {
                if (!e.target.value.trim()) return;
                const val = Number(e.target.value);
                if (isNaN(val)) return;
                setAmount(val);
              }}
              placeholder={t("PAYMENT.CUSTOM_AMOUNT", "Custom amount")}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {creditMode
                ? t(
                    "PAYMENT.ADD_BALANCE_MIN_HELPER_CREDITS",
                    "Minimum top-up is {{min}} credits. Unused credit never expires.",
                    { min: ADD_BALANCE_MIN_USD },
                  )
                : t(
                    "PAYMENT.ADD_BALANCE_MIN_HELPER",
                    "Minimum top-up is ${{min}}. Unused credit never expires.",
                    { min: ADD_BALANCE_MIN_USD },
                  )}
            </p>
          </div>

          <div className="flex flex-col gap-3 mt-6" role="group" aria-labelledby="add-balance-method-label">
            <p id="add-balance-method-label" className="text-sm font-medium">
              {t("PAYMENT_SETTINGS.PAYMENT_METHOD", "Payment Method")}
            </p>
            <SelectSource
              onSelect={setSelectedSource}
              onUserSelect={(source) => {
                userPickedRef.current = true;
                pushDataLayer("Balance", "MethodSelect", undefined, undefined, { payment_method: source?.type });
              }}
              showBalance={false}
              autoSelectDefault
            />
            {canAddCard && canManageBilling && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => { pushDataLayer("Balance", "CardFormShown"); setAddSourceOpen(true); }}
                disabled={isPending}
              >
                {t("PAYMENT.ADD_CARD", "Add Card")}
              </Button>
            )}
            {canAddPaypal && canManageBilling && (  
              <ConnectPaypalButton
                layout="full-width"
                canManageBilling={canManageBilling}
                onSuccess={(newSource) => {
                  if (newSource && !userPickedRef.current) {
                    setSelectedSource(newSource);
                  }
                }}
              />
            )}
          </div>

          {error && <p className="text-sm text-danger mt-4" role="alert">{error}</p>}

          <p className="text-xs text-muted-foreground mt-6">
            Set it and forget it. Enable auto-reload to keep your balance topped up automatically.{" "}
            <button
              type="button"
              onClick={() => setAutoReloadOpen(true)}
              className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
            >
              Set up auto-reload
            </button>
          </p>
        </DialogBody>

        <DialogFooter>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              {t("ACTION.CANCEL", "Cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isPending || amount < ADD_BALANCE_MIN_USD || !canManageBilling}
            >
              {isPending
                ? t("ACTION.PROCESSING")
                : creditMode
                  ? `Add $${amount.toFixed(2)}`
                  : `Add $${amount.toFixed(2)} to Balance`}
            </Button>
          </div>
        </DialogFooter>
      </Dialog>

      <AddSource
        open={addSourceOpen}
        onClose={() => { setAddSourceOpen(false); setError(""); }}
        canManageBilling={canManageBilling}
        onSuccess={(newSource) => {
          trackFunnel("payment", "add_card_from_add_balance", { trigger: "no_card_escape" });
          pushDataLayer("Balance", "CardSaved");
          if (newSource && (newSource.type === "card" || !userPickedRef.current)) {
            setSelectedSource(newSource);
          }
          setAddSourceOpen(false);
          setError("");
        }}
      />

      <Dialog
        open={autoReloadOpen}
        onClose={() => setAutoReloadOpen(false)}
        size="lg"
        aria-label={t("PAYMENT_SETTINGS.RELOAD.TITLE", "Auto-reload")}
        aria-modal="true"
        data-testid="auto-reload-dialog"
      >
        <DialogBody>
          <AutoReload canManageBilling={canManageBilling} />
        </DialogBody>
        <DialogFooter>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAutoReloadOpen(false)}
            >
              {t("ACTION.CLOSE", "Close")}
            </Button>
          </div>
        </DialogFooter>
      </Dialog>
    </>
  );
}
